import { type MouseEvent, useCallback } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { isViewTransitionActive } from "../lib/abort-safe-view-transitions";

/**
 * Navigates inside a view transition, so elements named on both pages morph
 * between them. BrowserRouter ignores the `viewTransition` prop, which only a
 * data router honours, so the transition is started here instead.
 */
export function useMorphNavigate() {
  const navigate = useNavigate();
  const go = useCallback(
    (to: string) => {
      const start = document.startViewTransition?.bind(document);
      if (!start || isViewTransitionActive()) {
        navigate(to);
        return;
      }
      start(() => flushSync(() => navigate(to)));
    },
    [navigate],
  );
  /** For a Link's onClick: a plain left click morphs, anything else (new tab, new window) is left to the browser. */
  const onLinkClick = useCallback(
    (to: string) => (e: MouseEvent<HTMLAnchorElement>) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      go(to);
    },
    [go],
  );
  return { go, onLinkClick };
}
